"use client";

import { Icon, StatusBadge, cn } from "@repo/dashboard-core";
import Image from "next/image";
import { useTranslations } from "next-intl";
import { useMemo, useState } from "react";

import type { PinDetails } from "@/lib/types/topup";

import { CopyableValue } from "./CopyableValue";
import { OngoingFAQ } from "./OngoingFAQ";

function normalizeInstruction(str: string): string {
  const instruction = str.trimEnd().endsWith(",") ? str.slice(0, -1) : str;
  return instruction.replaceAll("<br>", "").replace(/<br\s*\/?>/gi, "");
}

function getInstructions(pinDetails: PinDetails): string[] {
  return Object.keys(pinDetails)
    .filter((key) => key.includes("info"))
    .flatMap((key) => {
      const val = pinDetails[key];
      return Array.isArray(val) ? val : [val];
    })
    .filter(
      (item): item is string => item !== null && typeof item === "string",
    );
}

function ExpandedDetails({
  pinDetails,
  instructions,
}: {
  pinDetails: PinDetails | null;
  instructions: string[];
}) {
  const t = useTranslations("dashboard");

  if (!pinDetails) return null;

  const code = pinDetails.code;
  const serial = pinDetails.serial;

  if (!code && instructions.length === 0) return null;

  return (
    <div className="mt-3 flex w-full flex-col items-start gap-3 border-t border-gray-200 pt-3">
      {code && (
        <dl className="flex w-full flex-col gap-2 rounded-xl bg-surface-hover p-3">
          <div className="flex items-center justify-between">
            <dt className="text-sm font-semibold text-text-secondary">
              {t("topup.code")}:
            </dt>
            <dd>
              <CopyableValue
                value={code}
                className="text-sm font-bold text-primary"
              />
            </dd>
          </div>
          {serial && (
            <div className="flex items-center justify-between">
              <dt className="text-sm font-semibold text-text-secondary">
                {t("topup.serial")}:
              </dt>
              <dd>
                <CopyableValue
                  value={serial}
                  className="text-sm font-bold text-primary"
                />
              </dd>
            </div>
          )}
        </dl>
      )}
      {instructions.length > 0 && (
        <div className="flex w-full flex-col items-start gap-1 text-start text-xs text-text-muted">
          {instructions.map((info) =>
            info.split(/ or /i).map((item, index) => (
              <span key={`${info}-${index}`}>
                {index !== 0 && (
                  <span className="my-1 text-xs font-bold uppercase text-primary">
                    {t("topup.or")}{" "}
                  </span>
                )}
                {normalizeInstruction(item)}
              </span>
            )),
          )}
        </div>
      )}
    </div>
  );
}

export function TopUpCard({
  title,
  image,
  date,
  pinDetails,
  amount,
  deliveryAmount,
  isOngoing,
  showFaq = false,
  className,
  onExpand,
}: {
  title: string;
  image: string;
  date: string;
  pinDetails: PinDetails | null;
  amount?: string;
  deliveryAmount?: string;
  isOngoing: boolean;
  showFaq?: boolean;
  className?: string;
  onExpand?: () => void;
}) {
  const t = useTranslations("dashboard");
  const [expanded, setExpanded] = useState(false);

  const instructions = useMemo(
    () => (pinDetails ? getInstructions(pinDetails) : []),
    [pinDetails],
  );

  const expandable =
    isOngoing || !pinDetails || !!pinDetails.code || instructions.length > 0;

  const toggle = () => {
    if (expandable) {
      const willExpand = !expanded;
      setExpanded(willExpand);
      if (willExpand) onExpand?.();
    }
  };

  return (
    <div
      data-testid="topup-card"
      role={expandable ? "button" : undefined}
      tabIndex={expandable ? 0 : undefined}
      aria-expanded={expandable ? expanded : undefined}
      onClick={toggle}
      onKeyDown={
        expandable
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                toggle();
              }
            }
          : undefined
      }
      className={cn(
        // On wider screens the card takes the list's columns (see page.tsx),
        // so amounts and statuses line up from one card to the next.
        "group rounded-xl p-4 transition-all duration-200 sm:col-span-full sm:grid sm:grid-cols-subgrid sm:*:col-span-full",
        expandable && "cursor-pointer",
        expanded
          ? "bg-primary/5 ring-1 ring-primary/15"
          : "hover:bg-surface-hover",
        className,
      )}
    >
      {/* Main row */}
      <div className="flex flex-col gap-3 sm:grid sm:grid-cols-subgrid sm:items-center sm:gap-4">
        {/* Top: logo + title */}
        <div className="flex min-w-0 items-center gap-3">
          {image && (
            <Image
              src={image}
              alt={title}
              width={80}
              height={80}
              className="h-10 w-10 shrink-0 rounded-lg object-contain"
            />
          )}
          {/* break-words: next to the pill, a long operator name can be
              wider than its column (640px, Portuguese). */}
          <div className="min-w-0 flex-1 break-words">
            <p className="font-medium text-text-primary">{title}</p>
            <p className="text-sm text-text-muted">
              {t("topup.mobile-recharge")}
            </p>
          </div>
        </div>

        {/* Bottom: amounts + status + date + "View details". On a phone the
            pill takes its own line, right-aligned under the status: beside it
            there is no room in most languages. */}
        <div className="flex shrink-0 flex-wrap items-center justify-end gap-x-4 gap-y-2 sm:contents">
          {/* Amounts */}
          <div className="mr-auto text-left sm:col-start-2 sm:mr-0 sm:text-right">
            {amount && (
              <p className="font-semibold text-text-primary">{amount}</p>
            )}
            {deliveryAmount && (
              <p className="text-xs text-text-muted">{deliveryAmount}</p>
            )}
          </div>

          {/* Status + date */}
          <div className="flex flex-col items-end gap-1 sm:col-start-3">
            <StatusBadge
              label={isOngoing ? t("topup.ongoing") : t("topup.delivered")}
              variant={isOngoing ? "warning" : "success"}
            />
            <span className="text-xs text-text-muted">{date}</span>
          </div>

          {/* GD-121 — a bare chevron read as decoration, so most customers
              never opened the row. The whole row stays the button; the pill
              only labels it. */}
          {expandable && (
            <div className="flex basis-full justify-end sm:contents">
              <span
                data-testid="topup-card-view-details"
                className="inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-full border border-accent/35 bg-accent-light px-3 py-1 text-sm font-medium text-accent-dark transition-colors group-hover:bg-accent/15 sm:col-start-4"
              >
                {t("topup.view-details")}
                <Icon
                  name="chevronDown"
                  size={14}
                  className={cn(
                    "transition-transform duration-200",
                    expanded && "rotate-180",
                  )}
                />
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Expandable details */}
      {expanded && (
        <div
          data-testid="topup-card-details"
          onClick={(e) => e.stopPropagation()}
        >
          <ExpandedDetails
            pinDetails={pinDetails}
            instructions={instructions}
          />

          {!pinDetails?.code && !isOngoing && (
            <p className="mt-2 text-xs text-text-muted">
              {t("topup.no-pin-message")}
            </p>
          )}
          {isOngoing && (
            <p className="mt-2 text-xs text-text-muted">
              {t("topup.ongoing-message")}
            </p>
          )}
        </div>
      )}

      {/* Reassurance FAQ — only on the latest ongoing top-up */}
      {isOngoing && showFaq && <OngoingFAQ productName={title} />}
    </div>
  );
}
